export type PlaygroundFiles = Record<string, string>;

const WRANGLER_BUILD_CONFIG = `import { defineWranglerConfig } from "wrangler/experimental-config";

export default defineWranglerConfig({
  minify: true,
  uploadSourceMaps: true
});
`;

const COUNTER_CLIENT_SOURCE = String.raw`
const configNode = document.querySelector("#counter-config");
const config = JSON.parse(configNode?.textContent || "{}");
// Resolve against the document, not a mount baked in at generation time: the
// same Worker is served both path-mounted (/<station>/<workspace>/) and at the
// root of its own preview subdomain, and a baked prefix 404s on the latter.
// An absolute apiUrl still wins — that points at a different origin entirely.
const apiBase = /^https?:/i.test(config.apiUrl || "")
  ? new URL(config.apiUrl, window.location.href)
  : new URL(".", window.location.href);
const valueNode = document.querySelector("#counter-value");
const revisionNode = document.querySelector("#counter-revision");
const syncNode = document.querySelector("#sync-status");
const workflowNode = document.querySelector("#workflow-status");
const connectionNode = document.querySelector("#connection-state");
const controls = [...document.querySelectorAll("[data-delta]")];
const celebrate = document.querySelector("#celebrate");
const shoutoutInput = document.querySelector("#celebrate-shoutout");
const renderBrowser = document.querySelector("#render-browser");
const recordTour = document.querySelector("#record-tour");
const tourStatus = document.querySelector("#tour-status");
const browserStatus = document.querySelector("#browser-status");
const browserPreview = document.querySelector("#browser-preview");
const inspectCapabilities = document.querySelector("#inspect-capabilities");
const capabilitiesStatus = document.querySelector("#capabilities-status");
const capabilitiesOutput = document.querySelector("#capabilities-output");
const runContainer = document.querySelector("#run-container");
const destroyContainer = document.querySelector("#destroy-container");
const containerStatus = document.querySelector("#container-status");
const containerOutput = document.querySelector("#container-output");
const stages = [...document.querySelectorAll("[data-workflow-stage]")];
let socket;
let browserObjectUrl;
let reconnectDelay = 300;

function endpoint(path) {
  return new URL(path.startsWith("/") ? path.slice(1) : path, apiBase);
}

function setControlsDisabled(disabled) {
  for (const button of controls) button.disabled = disabled;
}

function setWorkflowStage(stage) {
  const order = ["queued", "running", "broadcast"];
  const activeIndex = order.indexOf(stage);
  for (const item of stages) {
    const index = order.indexOf(item.dataset.workflowStage);
    item.classList.toggle("active", index <= activeIndex);
  }
}

function renderCounter(message) {
  const previous = valueNode.textContent;
  valueNode.textContent = String(message.value);
  revisionNode.textContent = "revision " + message.revision;
  valueNode.classList.toggle("changed", previous !== String(message.value));
  window.setTimeout(() => valueNode.classList.remove("changed"), 280);
  setControlsDisabled(false);
  syncNode.textContent = "WebSocket confirmed. A Queue is archiving the event to R2.";
  syncNode.dataset.state = "synced";
}

function renderCelebration(message) {
  setWorkflowStage("broadcast");
  workflowNode.textContent = message.message + " · " + message.theme + " · " + message.sparks + " sparks";
  workflowNode.dataset.state = "complete";
  celebrate.disabled = false;
}

function connect() {
  const url = endpoint("api/counter/socket");
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  connectionNode.textContent = "connecting";
  connectionNode.dataset.state = "connecting";
  socket = new WebSocket(url);
  socket.addEventListener("open", () => {
    reconnectDelay = 300;
    connectionNode.textContent = "live";
    connectionNode.dataset.state = "live";
    socket.send("sync");
  });
  socket.addEventListener("message", (event) => {
    let message;
    try { message = JSON.parse(event.data); } catch { return; }
    if (message.type === "counter") renderCounter(message);
    if (message.type === "celebration") renderCelebration(message);
  });
  socket.addEventListener("close", () => {
    connectionNode.textContent = "reconnecting";
    connectionNode.dataset.state = "connecting";
    setControlsDisabled(true);
    window.setTimeout(connect, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 5000);
  });
  socket.addEventListener("error", () => socket.close());
}

for (const button of controls) {
  button.addEventListener("click", async () => {
    try {
      const response = await fetch(endpoint("api/counter"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ delta: Number(button.dataset.delta) })
      });
      if (!response.ok) throw new Error("HTTP " + response.status);
      // Keep controls available so mutations can queue while the WebSocket
      // remains the authoritative source for values and revisions.
      await response.json();
    } catch (error) {
      syncNode.textContent = "Mutation failed: " + (error instanceof Error ? error.message : String(error));
      syncNode.dataset.state = "error";
    }
  });
}

celebrate.addEventListener("click", async () => {
  celebrate.disabled = true;
  setWorkflowStage("queued");
  workflowNode.textContent = "Workflow queued…";
  workflowNode.dataset.state = "running";
  try {
    const response = await fetch(endpoint("api/workflows/celebrate"), { method: "POST" });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "HTTP " + response.status);
    setWorkflowStage("running");
    workflowNode.textContent = "Workflow " + body.id.slice(0, 8) + " is building a celebration…";
    // Demonstrate sendEvent: if the caller typed a shoutout, send it to the
    // running Workflow so its step.waitForEvent enriches the broadcast.
    const shoutout = shoutoutInput?.value.trim();
    if (shoutout) {
      await fetch(endpoint("api/workflows/" + body.id + "/events"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ type: "celebration-customization", payload: { shoutout } })
      });
    }
  } catch (error) {
    celebrate.disabled = false;
    workflowNode.textContent = "Workflow failed: " + (error instanceof Error ? error.message : String(error));
    workflowNode.dataset.state = "error";
  }
});

renderBrowser.addEventListener("click", async () => {
  renderBrowser.disabled = true;
  browserStatus.textContent = "Browser Run is rendering a fresh PNG…";
  browserStatus.dataset.state = "running";
  try {
    const response = await fetch(endpoint("api/browser/counter-card"), {
      method: "POST"
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "HTTP " + response.status);
    }
    if (browserObjectUrl) URL.revokeObjectURL(browserObjectUrl);
    browserObjectUrl = URL.createObjectURL(await response.blob());
    browserPreview.src = browserObjectUrl;
    browserPreview.hidden = false;
    browserStatus.textContent = "Rendered server-side with the BROWSER binding.";
    browserStatus.dataset.state = "complete";
  } catch (error) {
    browserStatus.textContent = "Browser render failed: " + (error instanceof Error ? error.message : String(error));
    browserStatus.dataset.state = "error";
  } finally {
    renderBrowser.disabled = false;
  }
});

recordTour.addEventListener("click", async () => {
  recordTour.disabled = true;
  tourStatus.textContent = "Driving a Browser Run session (recording)…";
  tourStatus.dataset.state = "running";
  try {
    const response = await fetch(endpoint("api/browser/tour"), { method: "POST" });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "HTTP " + response.status);
    if (body.recorded) {
      tourStatus.textContent = "Session recorded — open the Flow recordings tab to watch it.";
      tourStatus.dataset.state = "complete";
    } else {
      tourStatus.textContent = "Browser Run unavailable: " + (body.reason || "no session");
      tourStatus.dataset.state = "error";
    }
  } catch (error) {
    tourStatus.textContent = "Browser tour failed: " + (error instanceof Error ? error.message : String(error));
    tourStatus.dataset.state = "error";
  } finally {
    recordTour.disabled = false;
  }
});

inspectCapabilities.addEventListener("click", async () => {
  inspectCapabilities.disabled = true;
  capabilitiesStatus.textContent = "Reading scoped KV and bundled ASSETS…";
  capabilitiesStatus.dataset.state = "running";
  try {
    const response = await fetch(endpoint("api/capabilities"));
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || "HTTP " + response.status);
    capabilitiesOutput.textContent = JSON.stringify(body, null, 2);
    capabilitiesOutput.hidden = false;
    capabilitiesStatus.textContent = "KV metadata, key listing, and static asset fetch completed.";
    capabilitiesStatus.dataset.state = "complete";
  } catch (error) {
    capabilitiesStatus.textContent = "Capability inspection failed: " + (error instanceof Error ? error.message : String(error));
    capabilitiesStatus.dataset.state = "error";
  } finally {
    inspectCapabilities.disabled = false;
  }
});

async function containerJson(path, init) {
  const response = await fetch(endpoint(path), init);
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "HTTP " + response.status);
  return body;
}

function containerSocketRoundTrip() {
  const url = endpoint("container/socket");
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Container WebSocket timeout")), 30000);
    const ws = new WebSocket(url);
    ws.addEventListener("open", () => ws.send("dashboard-ping"));
    ws.addEventListener("message", (event) => {
      clearTimeout(timer);
      resolve(String(event.data));
      ws.close(1000, "round trip complete");
    });
    ws.addEventListener("error", () => {
      clearTimeout(timer);
      reject(new Error("Container WebSocket failed"));
    });
  });
}

runContainer.addEventListener("click", async () => {
  runContainer.disabled = true;
  destroyContainer.disabled = true;
  containerStatus.textContent = "Building the Dockerfile and starting an isolated Sandbox…";
  containerStatus.dataset.state = "running";
  try {
    const started = await containerJson("container/control/start", { method: "POST" });
    const health = await containerJson("container/health");
    const written = await containerJson("container/files", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ source: "dashboard", message: "ephemeral demo" })
    });
    const files = await containerJson("container/files");
    const websocket = await containerSocketRoundTrip();
    containerOutput.textContent = JSON.stringify(
      { started, health, written, files, websocket },
      null,
      2
    );
    containerOutput.hidden = false;
    containerStatus.textContent = "Docker lifecycle + HTTP + files + WebSocket completed.";
    containerStatus.dataset.state = "complete";
  } catch (error) {
    containerStatus.textContent = "Container demo failed: " + (error instanceof Error ? error.message : String(error));
    containerStatus.dataset.state = "error";
  } finally {
    runContainer.disabled = false;
    destroyContainer.disabled = false;
  }
});

destroyContainer.addEventListener("click", async () => {
  destroyContainer.disabled = true;
  containerStatus.textContent = "Destroying the inner Container and outer Sandbox…";
  containerStatus.dataset.state = "running";
  try {
    const body = await containerJson("container/control/destroy", { method: "POST" });
    containerStatus.textContent = "Container destroyed; the next request performs a clean rebuild.";
    containerStatus.dataset.state = "complete";
    containerOutput.textContent = JSON.stringify(body, null, 2);
    containerOutput.hidden = false;
  } catch (error) {
    containerStatus.textContent = "Container cleanup failed: " + (error instanceof Error ? error.message : String(error));
    containerStatus.dataset.state = "error";
  } finally {
    destroyContainer.disabled = false;
  }
});

connect();
`;

// Worker names double as routes (/<session>/<name>/...), so each new
// workerspace gets a unique name to prevent sibling collisions.
export function makeDefaultFiles(): PlaygroundFiles {
  const workerName = `counter-api-${Date.now()}`;
  return {
    "wrangler.config.ts": WRANGLER_BUILD_CONFIG,
    "src/counter.ts": `import { DurableObject } from "cloudflare:workers";

export interface CounterSnapshot {
  value: number;
  revision: number;
  updatedAt: number;
}

export interface Celebration {
  value: number;
  message: string;
  theme: string;
  sparks: number;
  completedAt: number;
}

export class Counter extends DurableObject<Env> {
  constructor(ctx: DurableObjectState, env: Env) {
    super(ctx, env);
    // SQLite-backed Durable Objects keep related state queryable and atomic.
    this.ctx.storage.sql.exec(
      "CREATE TABLE IF NOT EXISTS counter_state (singleton INTEGER PRIMARY KEY CHECK (singleton = 1), value INTEGER NOT NULL, revision INTEGER NOT NULL, updated_at INTEGER NOT NULL)"
    );
    this.ctx.storage.sql.exec(
      "INSERT OR IGNORE INTO counter_state VALUES (1, 0, 0, ?)",
      Date.now()
    );
    this.ctx.storage.sql.exec(
      "CREATE TABLE IF NOT EXISTS celebrations (singleton INTEGER PRIMARY KEY CHECK (singleton = 1), value INTEGER NOT NULL, message TEXT NOT NULL, theme TEXT NOT NULL, sparks INTEGER NOT NULL, completed_at INTEGER NOT NULL)"
    );
  }

  private readSnapshot(): CounterSnapshot {
    return this.ctx.storage.sql
      .exec<CounterSnapshot & Record<string, number>>(
        "SELECT value, revision, updated_at AS updatedAt FROM counter_state WHERE singleton = 1"
      )
      .one();
  }

  async snapshot(): Promise<CounterSnapshot> {
    return this.readSnapshot();
  }

  async change(delta: number): Promise<CounterSnapshot> {
    if (delta !== 1 && delta !== -1) {
      throw new RangeError("Counter delta must be +1 or -1.");
    }
    const next = this.ctx.storage.transactionSync(() => {
      this.ctx.storage.sql.exec(
        "UPDATE counter_state SET value = value + ?, revision = revision + 1, updated_at = ? WHERE singleton = 1",
        delta,
        Date.now()
      );
      return this.readSnapshot();
    });

    // Make the asynchronous source of truth visible in the demo. The HTTP
    // mutation waits briefly, then the hibernating sockets publish the state.
    // Kept short so the starter's tests stay snappy while still demonstrating
    // the async broadcast ordering.
    await new Promise((resolve) => setTimeout(resolve, 150));
    this.broadcast({ type: "counter", ...next });
    return next;
  }

  async announce(celebration: Celebration): Promise<void> {
    this.ctx.storage.sql.exec(
      "INSERT INTO celebrations VALUES (1, ?, ?, ?, ?, ?) ON CONFLICT(singleton) DO UPDATE SET value = excluded.value, message = excluded.message, theme = excluded.theme, sparks = excluded.sparks, completed_at = excluded.completed_at",
      celebration.value,
      celebration.message,
      celebration.theme,
      celebration.sparks,
      celebration.completedAt
    );
    this.broadcast({ type: "celebration", ...celebration });
  }

  async reset(): Promise<void> {
    this.ctx.storage.transactionSync(() => {
      this.ctx.storage.sql.exec(
        "UPDATE counter_state SET value = 0, revision = 0, updated_at = ? WHERE singleton = 1",
        Date.now()
      );
      this.ctx.storage.sql.exec("DELETE FROM celebrations");
    });
  }

  async fetch(request: Request): Promise<Response> {
    if (request.headers.get("Upgrade")?.toLowerCase() !== "websocket") {
      return Response.json(await this.snapshot());
    }

    const pair = new WebSocketPair();
    const [client, server] = Object.values(pair);
    server.serializeAttachment({ connectedAt: Date.now(), channel: "counter" });
    this.ctx.acceptWebSocket(server, ["counter"]);
    server.send(JSON.stringify({ type: "counter", ...(await this.snapshot()) }));
    const celebration = this.ctx.storage.sql
      .exec<Celebration & Record<string, string | number>>(
        "SELECT value, message, theme, sparks, completed_at AS completedAt FROM celebrations WHERE singleton = 1"
      )
      .toArray()[0];
    if (celebration) {
      server.send(JSON.stringify({ type: "celebration", ...celebration }));
    }
    return new Response(null, { status: 101, webSocket: client });
  }

  async webSocketMessage(socket: WebSocket, message: string | ArrayBuffer) {
    if (String(message) === "sync") {
      socket.send(JSON.stringify({ type: "counter", ...(await this.snapshot()) }));
    }
  }

  webSocketClose() {}
  webSocketError() {}

  private broadcast(message: unknown) {
    const encoded = JSON.stringify(message);
    for (const socket of this.ctx.getWebSockets("counter")) {
      try {
        socket.send(encoded);
      } catch {
        // Hibernation removes closed sockets; a close racing this broadcast is harmless.
      }
    }
  }
}
`,
    "src/workflow.ts": `import { WorkflowEntrypoint } from "cloudflare:workers";
import type { Celebration } from "./counter";

interface CelebrationParams {
  requestedValue: number;
  reason: "manual" | "milestone";
}

// Optional enrichment delivered while the Workflow runs. Send it with
// instance.sendEvent (POST /api/workflows/:id/events) and the step.waitForEvent
// below consumes it.
export interface CelebrationCustomization {
  sparks?: number;
  shoutout?: string;
}

export class CounterCelebrationWorkflow extends WorkflowEntrypoint<
  Env,
  CelebrationParams
> {
  async run(event, step) {
    const observed = await step.do("read live counter", async () => {
      return this.env.COUNTER.getByName("global").snapshot();
    });

    // Demonstrate step.waitForEvent: an external caller can customize the
    // celebration while the Workflow runs by sending an event whose type
    // matches. waitForEvent resolves with a WorkflowStepEvent envelope, so the
    // customization the caller sent lives on event.payload. It throws on
    // timeout, so we catch it and proceed with the defaults below when no event
    // arrives. "1 second" is the shortest timeout Workflows allows, keeping the
    // starter snappy.
    let customization: CelebrationCustomization | undefined;
    try {
      const event = await step.waitForEvent<CelebrationCustomization>(
        "receive optional celebration customization",
        { type: "celebration-customization", timeout: "1 second" }
      );
      customization = event.payload;
    } catch {
      customization = undefined;
    }

    const [theme, sparks] = await Promise.all([
      step.do("choose a color theme", async () => {
        return observed.value >= 0 ? "aurora" : "midnight";
      }),
      step.do("calculate confetti", async () => {
        return customization?.sparks ?? Math.max(12, Math.abs(observed.value) * 7);
      })
    ]);

    await step.sleep("build dramatic suspense", "250 milliseconds");

    const celebration = await step.do("broadcast celebration", async () => {
      const defaultMessage =
        event.payload.reason === "milestone"
          ? "Milestone unlocked at " + observed.value + "!"
          : "Counter " + observed.value + " deserves a tiny parade!";
      const result: Celebration = {
        value: observed.value,
        message: customization?.shoutout || defaultMessage,
        theme,
        sparks,
        completedAt: Date.now()
      };
      await this.env.COUNTER.getByName("global").announce(result);
      return result;
    });

    return {
      requestedValue: event.payload.requestedValue,
      observedValue: observed.value,
      customized: Boolean(customization),
      celebration
    };
  }
}
`,
    "src/browser.ts": `import { env } from "cloudflare:workers";
import puppeteer from "@cloudflare/puppeteer";
import type { CounterSnapshot } from "./counter";

/** Render a self-contained counter card with Cloudflare Browser Run. */
export function renderCounterCard(snapshot: CounterSnapshot): Promise<Response> {
  const html = [
    "<!doctype html><html><head><meta charset='utf-8'><style>",
    "*{box-sizing:border-box}body{margin:0;min-height:100vh;display:grid;place-items:center;background:#080b18;color:#eef2ff;font-family:Inter,system-ui,sans-serif}",
    ".card{width:620px;padding:48px;border:1px solid #475569;border-radius:28px;background:linear-gradient(145deg,#111936,#261338);box-shadow:0 30px 80px #0008}",
    ".eyebrow{color:#67e8f9;font-size:14px;font-weight:800;letter-spacing:.16em;text-transform:uppercase}",
    ".value{margin:24px 0 10px;font:900 128px/1 ui-monospace,monospace;background:linear-gradient(90deg,#67e8f9,#c084fc,#fb7185);background-clip:text;color:transparent}",
    ".meta{color:#aab3cc;font:16px ui-monospace,monospace}",
    "</style></head><body><main class='card'>",
    "<div class='eyebrow'>Cloudflare Browser Run</div>",
    "<div class='value'>" + snapshot.value + "</div>",
    "<div class='meta'>revision " + snapshot.revision + " · " + new Date(snapshot.updatedAt).toISOString() + "</div>",
    "</main></body></html>"
  ].join("");

  return env.BROWSER.quickAction("screenshot", {
    html,
    viewport: { width: 720, height: 420, deviceScaleFactor: 1 },
    screenshotOptions: { type: "png", encoding: "binary", fullPage: true },
    cacheTTL: 0
  });
}

/**
 * Drive a real Browser Run session so the flow-recording sidecar captures a
 * watchable video. Unlike renderCounterCard's stateless quick action (which is
 * NOT recorded), a driven session — launch, render, animate — is recorded.
 * Hit POST /api/browser/tour, then open the "Flow recordings" tab to watch it
 * (or open it from the Test tab after a test run drives this endpoint).
 */
export async function driveBrowserTour(
  snapshot: CounterSnapshot
): Promise<Response> {
  const html = [
    "<!doctype html><html><head><meta charset='utf-8'><style>",
    "*{box-sizing:border-box}body{margin:0;min-height:100vh;display:grid;place-items:center;background:#080b18;color:#eef2ff;font-family:Inter,system-ui,sans-serif}",
    ".card{width:620px;padding:48px;border:1px solid #475569;border-radius:28px;background:linear-gradient(145deg,#111936,#261338);animation:pulse 1.4s ease-in-out infinite}",
    "@keyframes pulse{0%,100%{box-shadow:0 30px 80px #0008}50%{box-shadow:0 30px 120px #67e8f966}}",
    ".eyebrow{color:#67e8f9;font-size:14px;font-weight:800;letter-spacing:.16em;text-transform:uppercase}",
    ".value{margin:24px 0 10px;font:900 128px/1 ui-monospace,monospace;background:linear-gradient(90deg,#67e8f9,#c084fc,#fb7185);background-clip:text;color:transparent}",
    ".meta{color:#aab3cc;font:16px ui-monospace,monospace}",
    "</style></head><body><main class='card'>",
    "<div class='eyebrow'>Flow recording · Browser Run</div>",
    "<div class='value' id='v'>0</div>",
    "<div class='meta'>revision " + snapshot.revision + "</div>",
    "<script>",
    "var el=document.getElementById('v');var goal=(" + snapshot.value + ")||42;var n=0;",
    "var step=Math.max(1,Math.ceil(goal/50));",
    "var t=setInterval(function(){n=Math.min(goal,n+step);el.textContent=n;if(n>=goal)clearInterval(t);},50);",
    "</script></main></body></html>"
  ].join("");

  // Hard bound the whole session so a stuck browser launch/render can never hang
  // the request (Browser Run acquisition can block when capacity is unavailable,
  // and hosted-test timeouts are not always enforced). The Promise.race timeouts
  // below — not keep_alive — are what prevent hangs. keep_alive MUST be within
  // Cloudflare's 60-600s range: a value below 60s makes puppeteer.launch reject
  // immediately, which silently disables the recorded tour.
  const withTimeout = (ms, reason) =>
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(reason)), ms)
    );
  let browser;
  try {
    browser = await Promise.race([
      puppeteer.launch(env.BROWSER, { keep_alive: 60000 }),
      withTimeout(12000, "browser launch timed out")
    ]);
  } catch (error) {
    // Fail-soft: a Browser Run session needs a real browser (available in
    // production). Where none is available or it is slow to acquire, report
    // gracefully so callers/tests don't fault or hang — no session means no
    // recording, which is expected.
    return Response.json({
      tour: "unavailable",
      recorded: false,
      reason: (error instanceof Error ? error.message : String(error)).slice(0, 200)
    });
  }
  try {
    await Promise.race([
      (async () => {
        const page = await browser.newPage();
        await page.setViewport({ width: 1280, height: 720 });
        await page.setContent(html, { waitUntil: "load" });
        // Hold the page open so the animation plays and the recorder captures
        // frames before the session ends.
        await new Promise((resolve) => setTimeout(resolve, 3000));
      })(),
      withTimeout(15000, "browser tour timed out")
    ]);
    return Response.json({
      tour: "complete",
      recorded: true,
      hint: "Watch this session in the Flow recordings tab, or from the Test tab after a test run."
    });
  } catch (error) {
    return Response.json({
      tour: "unavailable",
      recorded: false,
      reason: (error instanceof Error ? error.message : String(error)).slice(0, 200)
    });
  } finally {
    await browser.close().catch(() => {});
  }
}
`,
    "src/index.ts": `import { WorkerEntrypoint, env } from "cloudflare:workers";
import { driveBrowserTour, renderCounterCard } from "./browser";
export { Counter } from "./counter";
export { CounterCelebrationWorkflow } from "./workflow";

interface CounterEvent {
  id: string;
  delta: number;
  value: number;
  revision: number;
  createdAt: number;
}

const counter = () => env.COUNTER.getByName("global");

const analyticsSchema =
  "CREATE TABLE IF NOT EXISTS counter_events (id TEXT PRIMARY KEY, delta INTEGER NOT NULL, value INTEGER NOT NULL, revision INTEGER NOT NULL, created_at INTEGER NOT NULL)";

async function recordAnalytics(event: CounterEvent) {
  const [, inserted] = await env.COUNTER_ANALYTICS.batch([
    env.COUNTER_ANALYTICS.prepare(analyticsSchema),
    env.COUNTER_ANALYTICS.prepare(
      "INSERT INTO counter_events (id, delta, value, revision, created_at) VALUES (?, ?, ?, ?, ?)"
    ).bind(event.id, event.delta, event.value, event.revision, event.createdAt)
  ]);
  return inserted.meta.last_row_id;
}

async function listAnalytics() {
  await env.COUNTER_ANALYTICS.prepare(analyticsSchema).run();
  return env.COUNTER_ANALYTICS.prepare(
    "SELECT id, delta, value, revision, created_at AS createdAt FROM counter_events ORDER BY revision DESC LIMIT 50"
  ).all();
}

async function inspectKvAndAssets(request: Request) {
  const key = "demo/theme";
  let flag = await env.COUNTER_FLAGS.getWithMetadata<{
    source: string;
    updatedAt: number;
  }>(key, "text");
  if (flag.value === null) {
    await env.COUNTER_FLAGS.put(key, "aurora", {
      expirationTtl: 86_400,
      metadata: { source: "default-template", updatedAt: Date.now() }
    });
    flag = await env.COUNTER_FLAGS.getWithMetadata(key, "text");
  }
  const keys = await env.COUNTER_FLAGS.list({ prefix: "demo/", limit: 20 });
  const assetUrl = new URL("/capabilities.json", request.url);
  const assetResponse = await env.ASSETS.fetch(assetUrl);
  const asset = assetResponse.ok ? await assetResponse.json() : null;
  return {
    kv: {
      key,
      value: flag.value,
      metadata: flag.metadata,
      keys: keys.keys.map(({ name, expiration, metadata }) => ({
        name,
        expiration,
        metadata
      })),
      listComplete: keys.list_complete
    },
    assets: {
      status: assetResponse.status,
      contentType: assetResponse.headers.get("content-type"),
      manifest: asset
    }
  };
}

async function updateFlag(request: Request) {
  const body = (await request.json()) as { key?: unknown; value?: unknown };
  const key = typeof body.key === "string" ? body.key : "";
  if (!/^demo\\/[a-z0-9-]{1,40}$/.test(key) || typeof body.value !== "string") {
    return Response.json({ error: "Use a demo/<name> key and string value." }, { status: 400 });
  }
  await env.COUNTER_FLAGS.put(key, body.value, {
    expirationTtl: 86_400,
    metadata: { source: "api", updatedAt: Date.now() }
  });
  return Response.json({ key, value: body.value, stored: true });
}

async function startCelebration(reason: "manual" | "milestone") {
  const snapshot = await counter().snapshot();
  const id = "celebrate-" + crypto.randomUUID();
  const instance = await env.COUNTER_CELEBRATION.create({
    id,
    params: { requestedValue: snapshot.value, reason }
  });
  return { id: instance.id, status: await instance.status() };
}

export default class CounterApi extends WorkerEntrypoint<Env> {
  async fetch(request: Request) {
    const url = new URL(request.url);

    if (url.pathname === "/api/counter/socket") {
      return counter().fetch(request);
    }
    if (url.pathname === "/api/counter" && request.method === "GET") {
      return Response.json(await counter().snapshot());
    }
    if (url.pathname === "/api/counter" && request.method === "POST") {
      const body = (await request.json()) as { delta?: unknown };
      const delta = Number(body.delta);
      const snapshot = await counter().change(delta);
      const event: CounterEvent = {
        id: crypto.randomUUID(),
        delta,
        value: snapshot.value,
        revision: snapshot.revision,
        createdAt: Date.now()
      };
      await env.COUNTER_EVENTS.send(event);
      const d1RowId = await recordAnalytics(event);
      return Response.json({
        accepted: true,
        revision: snapshot.revision,
        queuedEvent: event.id,
        d1RowId
      });
    }
    if (
      url.pathname === "/api/counter/analytics" &&
      request.method === "GET"
    ) {
      const result = await listAnalytics();
      return Response.json({ events: result.results, meta: result.meta });
    }
    if (url.pathname === "/api/counter/events" && request.method === "GET") {
      const archived = await env.COUNTER_ARCHIVE.list({
        prefix: "events/",
        limit: 50
      });
      return Response.json({
        events: archived.objects.map(({ key, size, uploaded }) => ({
          key,
          size,
          uploaded
        }))
      });
    }
    if (url.pathname === "/api/capabilities" && request.method === "GET") {
      return Response.json(await inspectKvAndAssets(request));
    }
    if (url.pathname === "/api/flags" && request.method === "PUT") {
      return updateFlag(request);
    }
    if (url.pathname.startsWith("/api/flags/") && request.method === "DELETE") {
      const key = decodeURIComponent(url.pathname.slice("/api/flags/".length));
      if (!key.startsWith("demo/")) {
        return Response.json({ error: "Only demo/* keys can be deleted." }, { status: 400 });
      }
      await env.COUNTER_FLAGS.delete(key);
      return Response.json({ key, deleted: true });
    }
    if (
      url.pathname === "/api/browser/counter-card" &&
      request.method === "POST"
    ) {
      return renderCounterCard(await counter().snapshot());
    }
    if (url.pathname === "/api/browser/tour" && request.method === "POST") {
      // Drives a real Browser Run session that the flow-recording sidecar
      // captures — watch it in the Flow recordings tab.
      return driveBrowserTour(await counter().snapshot());
    }
    if (
      url.pathname === "/api/workflows/celebrate" &&
      request.method === "POST"
    ) {
      return Response.json(await startCelebration("manual"), { status: 202 });
    }
    if (
      url.pathname.startsWith("/api/workflows/") &&
      url.pathname.endsWith("/events") &&
      request.method === "POST"
    ) {
      // Demonstrate instance.sendEvent: forward an event to a running Workflow
      // instance so its step.waitForEvent can consume it.
      const id = decodeURIComponent(
        url.pathname.slice("/api/workflows/".length, -"/events".length)
      );
      const body = (await request.json()) as { type?: string; payload?: unknown };
      if (!body.type) {
        return Response.json({ error: "Missing event type." }, { status: 400 });
      }
      const instance = await env.COUNTER_CELEBRATION.get(id);
      await instance.sendEvent({ type: body.type, payload: body.payload });
      return Response.json({ id, sent: true });
    }
    if (
      url.pathname.startsWith("/api/workflows/") &&
      request.method === "GET"
    ) {
      const id = decodeURIComponent(url.pathname.slice("/api/workflows/".length));
      const instance = await env.COUNTER_CELEBRATION.get(id);
      return Response.json({ id: instance.id, status: await instance.status() });
    }

    return Response.json({
      name: "Durable Counter API",
      message: env.GREETING,
      counter: await counter().snapshot(),
      websocket: "/api/counter/socket",
      workflow: "/api/workflows/celebrate",
      queue: "COUNTER_EVENTS",
      archive: "/api/counter/events",
      database: "/api/counter/analytics",
      kvAndAssets: "/api/capabilities",
      flags: "/api/flags",
      browser: "/api/browser/counter-card",
      browserTour: "/api/browser/tour"
    });
  }

  async queue(batch: MessageBatch<CounterEvent>) {
    for (const message of batch.messages) {
      try {
        const event = message.body;
        const key =
          "events/" + String(event.revision).padStart(12, "0") + "-" + event.id + ".json";
        await env.COUNTER_ARCHIVE.put(key, JSON.stringify(event, null, 2), {
          httpMetadata: { contentType: "application/json" },
          customMetadata: { revision: String(event.revision) }
        });
        message.ack();
      } catch (error) {
        console.error("Failed to archive counter event", message.id, error);
        message.retry();
      }
    }
  }
}
`,
    "public/capabilities.json": JSON.stringify(
      {
        template: "Workerstation capability kitchen sink",
        bindings: [
          "SQLite Durable Object",
          "D1",
          "KV",
          "R2",
          "Queue",
          "Workflow",
          "Browser Run",
          "ASSETS",
          "Worker service",
          "Container"
        ],
        note: "This file is served through the custom ASSETS binding."
      },
      null,
      2
    ),
    "fixtures/example/index.ts": `export const rootUrl = "https://example.com/";
export const counterUrl = "https://example.com/api/counter";
export const socketUrl = "https://example.com/api/counter/socket";
export const celebrationUrl = "https://example.com/api/workflows/celebrate";

export async function nextJsonMessage(
  socket: WebSocket,
  predicate: (message: any) => boolean = () => true
): Promise<any> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      socket.removeEventListener("message", onMessage);
      reject(new Error("Timed out waiting for a WebSocket message."));
    }, 15_000);
    function onMessage(event: MessageEvent) {
      const message = JSON.parse(String(event.data));
      if (!predicate(message)) return;
      clearTimeout(timer);
      socket.removeEventListener("message", onMessage);
      resolve(message);
    }
    socket.addEventListener("message", onMessage);
  });
}

export async function waitForWorkflow(instance: WorkflowInstance) {
  for (let attempt = 0; attempt < 100; attempt++) {
    const status = await instance.status();
    if (status.status === "complete" || status.status === "errored") {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  throw new Error("Workflow did not finish in time.");
}
`,
    "test/worker.test.ts": `import { env, SELF } from "cloudflare:test";
import { beforeAll, describe, expect, it } from "vitest";
import {
  counterUrl,
  nextJsonMessage,
  rootUrl,
  socketUrl
} from "../fixtures/example";

const counter = () => env.COUNTER.getByName("global");

// Each test file already receives fresh storage. Reset once, then only where a
// test needs the counter's initial state after an earlier mutation.
beforeAll(async () => {
  await counter().reset();
  const archived = await env.COUNTER_ARCHIVE.list({ prefix: "events/" });
  if (archived.objects.length) {
    await env.COUNTER_ARCHIVE.delete(archived.objects.map(({ key }) => key));
  }
  const flags = await env.COUNTER_FLAGS.list({ prefix: "demo/" });
  if (flags.keys.length) {
    await Promise.all(flags.keys.map(({ name }) => env.COUNTER_FLAGS.delete(name)));
  }
});

describe("Durable Counter runtime", () => {
  it("serves API metadata and initial Durable Object state", async () => {
    const response = await SELF.fetch(rootUrl);
    const body = await response.json();

    expect(response.status).toBe(200);
    expect(body.name).toBe("Durable Counter API");
    expect(body.counter).toMatchObject({ value: 0, revision: 0 });
    expect(body.database).toBe("/api/counter/analytics");
    expect(body.kvAndAssets).toBe("/api/capabilities");
    expect(body.browser).toBe("/api/browser/counter-card");

    const capabilities = await (await SELF.fetch(
      "https://example.com/api/capabilities"
    )).json();
    expect(capabilities.kv).toMatchObject({
      key: "demo/theme",
      value: "aurora",
      listComplete: true
    });
    expect(capabilities.assets).toMatchObject({
      status: 200,
      manifest: { template: "Workerstation capability kitchen sink" }
    });
  });

  it("serializes concurrent counter mutations", async () => {
    await Promise.all([counter().change(1), counter().change(1)]);

    expect(await counter().snapshot()).toMatchObject({ value: 2, revision: 2 });
  });

  it("sends Queue messages and stores objects in R2", async () => {
    const event = {
      id: crypto.randomUUID(),
      delta: 1,
      value: 1,
      revision: 1,
      createdAt: Date.now()
    };
    await env.COUNTER_EVENTS.send(event);

    await env.COUNTER_ARCHIVE.put(
      "events/runtime-test.json",
      JSON.stringify(event),
      { httpMetadata: { contentType: "application/json" } }
    );
    const archived = await env.COUNTER_ARCHIVE.get("events/runtime-test.json");
    expect(await archived!.json()).toEqual(event);
  });

  it("runs prepared and batched statements against emulated D1", async () => {
    await env.COUNTER_ANALYTICS.exec(
      "DROP TABLE IF EXISTS d1_runtime; CREATE TABLE d1_runtime (id INTEGER PRIMARY KEY, name TEXT NOT NULL);"
    );
    const results = await env.COUNTER_ANALYTICS.batch([
      env.COUNTER_ANALYTICS.prepare(
        "INSERT INTO d1_runtime (name) VALUES (?)"
      ).bind("Ada"),
      env.COUNTER_ANALYTICS.prepare(
        "INSERT INTO d1_runtime (name) VALUES (?)"
      ).bind("Grace")
    ]);
    const session = env.COUNTER_ANALYTICS.withSession("first-primary");
    const first = await session.prepare(
      "SELECT id, name FROM d1_runtime ORDER BY id"
    ).first() as { id: number; name: string } | null;
    const raw = await env.COUNTER_ANALYTICS.prepare(
      "SELECT name FROM d1_runtime ORDER BY id"
    ).raw({ columnNames: true });

    expect(results).toHaveLength(2);
    expect(results.every((result) => result.success)).toBe(true);
    expect(first).toEqual({ id: 1, name: "Ada" });
    expect(session.getBookmark()).toEqual(expect.any(String));
    expect(raw).toEqual([["name"], ["Ada"], ["Grace"]]);
  });

  it("publishes delayed state through a hibernating WebSocket", async () => {
    await counter().reset();
    const upgrade = await SELF.fetch(socketUrl, {
      headers: { Upgrade: "websocket" }
    });
    const socket = upgrade.webSocket!;
    socket.accept();
    expect(await nextJsonMessage(socket)).toMatchObject({
      type: "counter",
      value: 0,
      revision: 0
    });

    const updated = nextJsonMessage(
      socket,
      (message) => message.type === "counter" && message.revision === 1
    );
    const startedAt = Date.now();
    const response = await SELF.fetch(counterUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ delta: 1 })
    });
    const message = await updated;

    expect(response.status).toBe(200);
    expect(Date.now() - startedAt).toBeGreaterThanOrEqual(100);
    expect(message).toMatchObject({ value: 1, revision: 1 });
    socket.close(1000, "test complete");
  });
});

describe("Counter Celebration Workflow runtime", () => {
  it("starts a Workflow through the runtime binding", async () => {
    const requestedValue = (await counter().snapshot()).value;
    const instance = await env.COUNTER_CELEBRATION.create({
      id: "runtime-" + crypto.randomUUID(),
      params: { requestedValue, reason: "manual" }
    });
    const status = await instance.status();

    expect(instance.id).toContain("runtime-");
    expect(["queued", "running", "waiting", "complete"]).toContain(status.status);
  });
});
`,
    "test/worker.e2e.test.ts": `import {
  afterAll,
  beforeAll,
  describe,
  expect,
  it
} from "vitest";
import { createTestHarness } from "wrangler";
import {
  counterUrl,
  nextJsonMessage,
  rootUrl,
  socketUrl
} from "../fixtures/example";

const server = createTestHarness({
  workers: [
    {
      config: {
        name: "${workerName}",
        main: "src/index.ts",
        compatibility_date: "2026-06-11",
        compatibility_flags: ["nodejs_compat"],
        vars: { GREETING: "Hello from workerspace 1" },
        kv_namespaces: [
          { binding: "COUNTER_FLAGS", id: "counter-flags" }
        ],
        assets: {
          directory: "public",
          binding: "ASSETS",
          run_worker_first: true
        },
        d1_databases: [
          {
            binding: "COUNTER_ANALYTICS",
            database_name: "counter-analytics",
            database_id: "00000000-0000-0000-0000-000000000001"
          }
        ],
        durable_objects: {
          bindings: [{ name: "COUNTER", class_name: "Counter" }]
        },
        browser: { binding: "BROWSER" },
        r2_buckets: [
          { binding: "COUNTER_ARCHIVE", bucket_name: "counter-archive" }
        ],
        queues: {
          producers: [
            { binding: "COUNTER_EVENTS", queue: "counter-events" }
          ],
          consumers: [
            {
              queue: "counter-events",
              max_batch_size: 10,
              max_batch_timeout: 1,
              max_retries: 3
            }
          ]
        },
        workflows: [
          {
            binding: "COUNTER_CELEBRATION",
            name: "${workerName}",
            class_name: "CounterCelebrationWorkflow"
          }
        ],
        migrations: [
          { tag: "v1", new_sqlite_classes: ["Counter"] }
        ]
      }
    }
  ]
});

beforeAll(() => server.listen());
// The E2E Worker is already isolated per file; reuse it across these serial tests.
afterAll(() => server.close());

describe("Durable Counter E2E", () => {
  it("serves the production-style API", async () => {
    const response = await server.fetch(rootUrl);
    expect(response.status).toBe(200);
    expect(await response.json()).toMatchObject({
      name: "Durable Counter API",
      counter: { value: 0, revision: 0 },
      database: "/api/counter/analytics",
      kvAndAssets: "/api/capabilities",
      browser: "/api/browser/counter-card"
    });

    const capabilities = await server.fetch("/api/capabilities");
    expect(capabilities.status).toBe(200);
    expect(await capabilities.json()).toMatchObject({
      kv: { key: "demo/theme", value: "aurora" },
      assets: { status: 200 }
    });
  });

  it("routes a hibernating WebSocket and HTTP mutation through the Worker", async () => {
    const upgrade = await server.fetch(socketUrl, {
      headers: { Upgrade: "websocket" }
    });
    const socket = upgrade.webSocket!;
    socket.accept();
    await nextJsonMessage(socket);
    const updated = nextJsonMessage(
      socket,
      (message) => message.type === "counter" && message.value === 1
    );

    const response = await server.fetch(counterUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ delta: 1 })
    });

    expect(response.status).toBe(200);
    expect(await updated).toMatchObject({ value: 1, revision: 1 });
    socket.close(1000, "test complete");
  });

  it("executes the production-style Workflow and exposes its status", async () => {
    const upgrade = await server.fetch(socketUrl, {
      headers: { Upgrade: "websocket" }
    });
    const socket = upgrade.webSocket!;
    socket.accept();
    await nextJsonMessage(socket);
    const announcement = nextJsonMessage(
      socket,
      (message) => message.type === "celebration"
    );

    const started = await server.fetch("/api/workflows/celebrate", {
      method: "POST"
    });
    const { id } = await started.json();
    // Demonstrate sendEvent: customize the running celebration. The Workflow's
    // step.waitForEvent consumes this event and enriches the broadcast.
    const customized = await server.fetch(
      "/api/workflows/" + encodeURIComponent(id) + "/events",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          type: "celebration-customization",
          payload: { sparks: 42, shoutout: "Confetti inbound!" }
        })
      }
    );
    expect(customized.status).toBe(200);
    let result: any;
    // The e2e harness is an in-process Miniflare gateway, so a tight poll
    // interval finishes promptly once the Workflow completes without adding
    // perceptible load.
    for (let attempt = 0; attempt < 100; attempt++) {
      result = await (
        await server.fetch("/api/workflows/" + encodeURIComponent(id))
      ).json();
      if (["complete", "errored"].includes(result.status.status)) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    expect(started.status).toBe(202);
    expect(result.id).toBe(id);
    expect(result.status.status).toBe("complete");
    expect(result.status.output.requestedValue).toBe(
      result.status.output.observedValue
    );
    expect(result.status.output.customized).toBe(true);
    expect(result.status.output.celebration).toMatchObject({
      theme: "aurora",
      sparks: 42
    });
    expect(result.status.output.celebration.message).toBe("Confetti inbound!");
    expect(await announcement).toMatchObject({
      type: "celebration",
      value: result.status.output.observedValue,
      theme: "aurora"
    });
    socket.close(1000, "test complete");
  }, 30_000);

  it("discovers the Counter Durable Object instance", async () => {
    await server.fetch(counterUrl);
    expect(
      await server.getWorker().listDurableObjectIds("COUNTER")
    ).toHaveLength(1);
  });

  it("drives a recorded Browser Run session (flow recording)", async () => {
    // Unlike the stateless quick-action screenshot, this drives a real Browser
    // Run session via puppeteer.launch(env.BROWSER), which the recording-enabled
    // binding captures — the recording surfaces in the Flow recordings tab and
    // from this test run in the Test tab. Where a browser is available the tour
    // completes and records; otherwise it reports gracefully.
    const response = await server.fetch(rootUrl + "api/browser/tour", {
      method: "POST"
    });
    expect(response.status).toBe(200);
    const body = await response.json();
    expect(["complete", "unavailable"]).toContain(body.tour);
    if (body.tour === "complete") {
      expect(body.recorded).toBe(true);
    }
  }, 30_000);
});
`,
    "cloudflare.config.ts": `import {
  bindings,
  defineWorker,
  exports as workerExports,
  triggers
} from "wrangler/experimental-config";
import * as entrypoint from "./src/index.ts" with { type: "cf-worker" };

export default defineWorker({
  name: "${workerName}",
  entrypoint,
  compatibilityDate: "2026-06-11",
  compatibilityFlags: ["nodejs_compat"],
  assets: {
    directory: "public",
    htmlHandling: "none",
    notFoundHandling: "none",
    runWorkerFirst: ["/api/*", "/"]
  },
  env: {
    GREETING: bindings.text("Hello from workerspace 1"),
    ASSETS: bindings.assets(),
    COUNTER_FLAGS: bindings.kv({ id: "counter-flags" }),
    COUNTER_ANALYTICS: bindings.d1({ name: "counter-analytics" }),
    COUNTER: bindings.durableObject({
      workerName: "${workerName}",
      exportName: "Counter"
    }),
    COUNTER_CELEBRATION: bindings.workflow({
      workerName: "${workerName}",
      exportName: "CounterCelebrationWorkflow"
    }),
    COUNTER_EVENTS: bindings.queue({ name: "counter-events" }),
    COUNTER_ARCHIVE: bindings.r2({ name: "counter-archive" }),
    // Flow recording is on: any Browser Run session this Worker drives (e.g.
    // puppeteer.launch(env.BROWSER)) is captured as pixel video + rrweb DOM
    // replay. Watch them in the "Flow recordings" tab, or open a test run's
    // recordings straight from the Test tab. Use "video" or "dom" for a single
    // mode. Stateless quick actions like screenshots are not recorded.
    BROWSER: bindings.browser({ recording: "both" })
  },
  exports: {
    // New Durable Objects use SQLite storage; Counter stores its state in SQL.
    Counter: workerExports.durableObject({ storage: "sqlite" })
  },
  triggers: [
    triggers.queue({
      name: "counter-events",
      maxBatchSize: 10,
      maxBatchTimeout: 1,
      maxRetries: 3
    })
  ]
});
`,
    "vitest.config.ts": `import { cloudflareTest } from "@cloudflare/vitest-pool-workers";
import { defineConfig } from "vitest/config";

export default defineConfig({
  test: {
    projects: [
      {
        plugins: [
          cloudflareTest({
            main: "./src/index.ts",
            miniflare: {
              compatibilityDate: "2026-06-11",
              compatibilityFlags: ["nodejs_compat"],
              bindings: { GREETING: "Hello from workerspace 1" },
              kvNamespaces: ["COUNTER_FLAGS"],
              durableObjects: { COUNTER: "Counter" },
              browserRendering: { binding: "BROWSER" },
              assets: {
                directory: "./public",
                binding: "ASSETS",
                routerConfig: {
                  has_user_worker: true,
                  invoke_user_worker_ahead_of_assets: true
                }
              },
              r2Buckets: ["COUNTER_ARCHIVE"],
              queueProducers: { COUNTER_EVENTS: "counter-events" },
              queueConsumers: {
                "counter-events": {
                  maxBatchSize: 10,
                  maxBatchTimeout: 1,
                  maxRetries: 3
                }
              },
              workflows: {
                COUNTER_CELEBRATION: {
                  name: "${workerName}",
                  className: "CounterCelebrationWorkflow"
                }
              }
            }
          })
        ],
        test: {
          name: "runtime",
          include: ["test/worker.test.ts"]
        }
      },
      {
        test: {
          name: "e2e",
          environment: "node",
          include: ["test/worker.e2e.test.ts"]
        }
      }
    ]
  }
});
`,
    "package.json": JSON.stringify(
      {
        name: "durable-counter-worker",
        private: true,
        type: "module",
        main: "src/index.ts",
        scripts: {
          dev: "wrangler dev --experimental-new-config",
          deploy: "wrangler deploy --experimental-new-config",
          test: "vitest"
        },
        dependencies: { "@cloudflare/puppeteer": "^1.1.0" },
        devDependencies: {
          "@cloudflare/vitest-pool-workers": "^0.18.4",
          vitest: "^4.1.0",
          wrangler: "^4.110.0"
        }
      },
      null,
      2
    )
  };
}

// Third starter capability: native Wrangler Container examples using both a
// text-only Docker build context and a Mesh-compatible approved remote image.
// They stay separate from the API so users can inspect, run, stop, and destroy
// each instance independently.
export function makeContainerDefaultFiles(): PlaygroundFiles {
  const workerName = `docker-service-${Date.now()}`;
  return {
    "src/index.ts": `import { env } from "cloudflare:workers";
import {
  Container,
  getContainer,
  switchPort
} from "@cloudflare/containers";

export class DemoContainer extends Container {
  override defaultPort = 8080;
  override requiredPorts = [8080];
  override sleepAfter = "2m";
  override envVars = {
    DEMO_MESSAGE: "Hello from a Dockerfile built inside Workerstation"
  };
}

// This approved remote image also runs through the normal Container API.
// Workerstation selects Sandbox or the operator-provisioned Mesh backend outside
// this nested Worker, so no Mesh credentials belong in this template.
export class MeshDemoContainer extends Container {
  override defaultPort = 8080;
  override requiredPorts = [8080];
  override sleepAfter = "2m";
  override entrypoint = [
    "/bin/sh",
    "-c",
    "body=workerstation-mesh-ready; while true; do printf 'HTTP/1.1 200 OK\\r\\nContent-Type: text/plain; charset=utf-8\\r\\nContent-Length: 24\\r\\nConnection: close\\r\\n\\r\\n%s' $body | nc -l -p 8080 >/dev/null; done"
  ];
}

type DemoKind = "dockerfile" | "mesh";

const demo = (kind: DemoKind) =>
  kind === "mesh"
    ? getContainer(env.MESH_DEMO_CONTAINER, "mesh-ready-demo")
    : getContainer(env.DEMO_CONTAINER, "dashboard-demo");

async function control(kind: DemoKind, action: string, method: string): Promise<Response> {
  const mutating = action === "start" || action === "stop" || action === "destroy";
  if (action !== "state" && !mutating) {
    return Response.json({ error: "Unknown Container action." }, { status: 404 });
  }
  if ((action === "state" && method !== "GET") || (mutating && method !== "POST")) {
    return Response.json(
      { error: "Use " + (action === "state" ? "GET" : "POST") + " for " + action + "." },
      {
        status: 405,
        headers: { allow: action === "state" ? "GET" : "POST" }
      }
    );
  }
  const container = demo(kind);
  if (action === "start") {
    await container.startAndWaitForPorts({
      ports: [8080],
      cancellationOptions: {
        instanceGetTimeoutMS: 20_000,
        portReadyTimeoutMS: 30_000,
        waitInterval: 250
      }
    });
  } else if (action === "stop") {
    await container.stop("SIGTERM");
  } else if (action === "destroy") {
    await container.destroy();
  }
  return Response.json({ action, state: await container.getState() });
}

export default {
  async fetch(request: Request): Promise<Response> {
    const url = new URL(request.url);
    if (url.pathname.startsWith("/mesh-container/control/")) {
      return control(
        "mesh",
        url.pathname.slice("/mesh-container/control/".length),
        request.method
      );
    }
    if (url.pathname.startsWith("/mesh-container/")) {
      const target = new URL(request.url);
      target.pathname = url.pathname.slice("/mesh-container".length) || "/";
      return demo("mesh").fetch(
        switchPort(new Request(target.toString(), request), 8080)
      );
    }
    if (url.pathname.startsWith("/container/control/")) {
      return control(
        "dockerfile",
        url.pathname.slice("/container/control/".length),
        request.method
      );
    }
    if (url.pathname.startsWith("/container/")) {
      const target = new URL(request.url);
      target.pathname = url.pathname.slice("/container".length) || "/";
      return demo("dockerfile").fetch(
        switchPort(new Request(target.toString(), request), 8080)
      );
    }
    return Response.json({
      name: "Container Capability Service",
      worker: "${workerName}",
      binding: "DEMO_CONTAINER",
      instance: "dashboard-demo",
      endpoints: {
        health: "/container/health",
        info: "/container/info",
        files: "/container/files",
        websocket: "/container/socket",
        state: "/container/control/state",
        start: "/container/control/start",
        stop: "/container/control/stop",
        destroy: "/container/control/destroy"
      },
      meshExample: {
        name: "Mesh-ready Alpine Container",
        image: "docker.io/library/alpine:3.22.1",
        binding: "MESH_DEMO_CONTAINER",
        instance: "mesh-ready-demo",
        endpoints: {
          response: "/mesh-container/health",
          state: "/mesh-container/control/state",
          start: "/mesh-container/control/start",
          stop: "/mesh-container/control/stop",
          destroy: "/mesh-container/control/destroy"
        },
        backendSelection:
          "Sandbox stays the default; an operator-enabled Workerstation routes the same Container calls through Mesh."
      },
      notes: [
        "The image is built from ./Dockerfile with image_build_context=.",
        "The Alpine example uses the same getContainer API and is compatible with the opt-in Mesh backend.",
        "HTTP and WebSockets use the same isolated Container instance.",
        "Writable files and locally built images are intentionally ephemeral."
      ]
    });
  }
};
`,
    "container/server.mjs": `import crypto from "node:crypto";
import fs from "node:fs";
import http from "node:http";
import os from "node:os";

const startedAt = Date.now();
const dataFile = "/tmp/workerstation-container-demo.json";

function json(response, value, status = 200) {
  response.writeHead(status, { "content-type": "application/json; charset=utf-8" });
  response.end(JSON.stringify(value, null, 2));
}

function readBody(request) {
  return new Promise((resolve, reject) => {
    let body = "";
    request.setEncoding("utf8");
    request.on("data", (chunk) => {
      body += chunk;
      if (body.length > 65536) reject(new Error("Body too large"));
    });
    request.on("end", () => resolve(body));
    request.on("error", reject);
  });
}

const server = http.createServer(async (request, response) => {
  const url = new URL(request.url || "/", "http://container.internal");
  if (url.pathname === "/health") {
    return json(response, {
      ok: true,
      source: "workerstation-dind-demo",
      message: process.env.DEMO_MESSAGE,
      startedAt,
      hostname: os.hostname(),
      pid: process.pid
    });
  }
  if (url.pathname === "/info") {
    return json(response, {
      node: process.version,
      platform: process.platform,
      architecture: process.arch,
      uptimeSeconds: Math.round(process.uptime()),
      environmentNames: Object.keys(process.env).sort()
    });
  }
  if (url.pathname === "/files" && request.method === "POST") {
    const value = JSON.parse(String(await readBody(request)) || "{}");
    const record = { value, writtenAt: Date.now() };
    fs.writeFileSync(dataFile, JSON.stringify(record));
    return json(response, { stored: true, ephemeral: true, record }, 201);
  }
  if (url.pathname === "/files") {
    const record = fs.existsSync(dataFile)
      ? JSON.parse(fs.readFileSync(dataFile, "utf8"))
      : null;
    return json(response, { record, ephemeral: true });
  }
  return json(response, {
    service: "Container Capability Service",
    routes: ["/health", "/info", "/files", "/socket"]
  });
});

server.on("upgrade", (request, socket) => {
  if (new URL(request.url || "/", "http://container.internal").pathname !== "/socket") {
    socket.destroy();
    return;
  }
  const accept = crypto
    .createHash("sha1")
    .update(String(request.headers["sec-websocket-key"]) + "258EAFA5-E914-47DA-95CA-C5AB0DC85B11")
    .digest("base64");
  socket.write(
    "HTTP/1.1 101 Switching Protocols\\r\\n" +
      "Upgrade: websocket\\r\\n" +
      "Connection: Upgrade\\r\\n" +
      "Sec-WebSocket-Accept: " + accept + "\\r\\n\\r\\n"
  );
  socket.on("data", (frame) => {
    const opcode = frame[0] & 15;
    if (opcode === 8) {
      socket.end(Buffer.from([0x88, 0]));
      return;
    }
    const length = frame[1] & 127;
    if (opcode !== 1 || length > 125) return socket.destroy();
    const mask = frame.subarray(2, 6);
    const payload = Buffer.alloc(length);
    for (let index = 0; index < length; index += 1) {
      payload[index] = frame[6 + index] ^ mask[index % 4];
    }
    const message = Buffer.from("container-echo:" + payload.toString());
    socket.write(Buffer.concat([Buffer.from([0x81, message.length]), message]));
  });
});

server.listen(8080, "0.0.0.0", () => {
  console.log("Dockerfile demo listening on :8080");
});
`,
    Dockerfile: `FROM node:22-alpine
WORKDIR /app
COPY container/server.mjs ./server.mjs
ENV NODE_ENV=production
EXPOSE 8080
HEALTHCHECK --interval=10s --timeout=2s --retries=3 CMD wget -q -O - http://127.0.0.1:8080/health || exit 1
USER node
CMD ["node", "server.mjs"]
`,
    ".dockerignore": `node_modules
.wrangler
.git
*.log
test
fixtures
README.md
`,
    "wrangler.jsonc": JSON.stringify(
      {
        name: workerName,
        main: "src/index.ts",
        compatibility_date: "2026-06-11",
        compatibility_flags: ["nodejs_compat"],
        minify: true,
        upload_source_maps: true,
        containers: [
          {
            class_name: "DemoContainer",
            image: "./Dockerfile",
            image_build_context: ".",
            max_instances: 2,
            instance_type: "standard-1"
          },
          {
            class_name: "MeshDemoContainer",
            image: "docker.io/library/alpine:3.22.1",
            max_instances: 1,
            instance_type: "standard-1"
          }
        ],
        durable_objects: {
          bindings: [
            { name: "DEMO_CONTAINER", class_name: "DemoContainer" },
            { name: "MESH_DEMO_CONTAINER", class_name: "MeshDemoContainer" }
          ]
        },
        migrations: [
          {
            tag: "v1",
            new_sqlite_classes: ["DemoContainer", "MeshDemoContainer"]
          }
        ]
      },
      null,
      2
    ),
    "env.d.ts": `interface Env {
  DEMO_CONTAINER: DurableObjectNamespace<import("./src/index").DemoContainer>;
  MESH_DEMO_CONTAINER: DurableObjectNamespace<import("./src/index").MeshDemoContainer>;
}
`,
    "fixtures/example/index.ts": `export const rootUrl = "https://example.com/";
export const healthUrl = "https://example.com/container/health";
export const socketUrl = "https://example.com/container/socket";
export const destroyUrl = "https://example.com/container/control/destroy";
export const meshHealthUrl = "https://example.com/mesh-container/health";
export const meshDestroyUrl = "https://example.com/mesh-container/control/destroy";
`,
    "test/worker.test.ts": `import { env, SELF } from "cloudflare:test";
import { describe, expect, it } from "vitest";
import { rootUrl } from "../fixtures/example";

describe("Dockerfile Container worker", () => {
  it("documents native Container routes without starting paid infrastructure", async () => {
    const response = await SELF.fetch(rootUrl);
    expect(response.status).toBe(200);
    expect(await response.json()).toMatchObject({
      name: "Container Capability Service",
      binding: "DEMO_CONTAINER",
      endpoints: {
        health: "/container/health",
        websocket: "/container/socket",
        destroy: "/container/control/destroy"
      },
      meshExample: {
        image: "docker.io/library/alpine:3.22.1",
        binding: "MESH_DEMO_CONTAINER",
        endpoints: {
          response: "/mesh-container/health",
          destroy: "/mesh-container/control/destroy"
        }
      }
    });

    const first = env.DEMO_CONTAINER.idFromName("dashboard-demo");
    const second = env.DEMO_CONTAINER.idFromString(first.toString());
    expect(first.equals(second)).toBe(true);
    expect(env.DEMO_CONTAINER.get(first)).toBeTruthy();
  });

  it("reports lifecycle state for both examples without provisioning paid infrastructure", async () => {
    for (const path of [
      "/container/control/state",
      "/mesh-container/control/state"
    ]) {
      const response = await SELF.fetch("https://example.com" + path);
      expect(response.status).toBe(200);
      expect(await response.json()).toMatchObject({
        action: "state",
        state: { status: "stopped" }
      });
    }
  });
});
`,
    "test/worker.e2e.test.ts": `import { afterAll, beforeAll, describe, expect, it } from "vitest";
import { createTestHarness } from "wrangler";

const server = createTestHarness({
  workers: [{
    config: {
      name: "${workerName}",
      main: "src/index.ts",
      compatibility_date: "2026-06-11",
      compatibility_flags: ["nodejs_compat"]
    }
  }]
});

beforeAll(() => server.listen());
afterAll(() => server.close());

describe("Dockerfile Container production-style worker", () => {
  it("serves metadata without provisioning a Container", async () => {
    const response = await server.fetch("/");
    expect(response.status).toBe(200);
    expect(await response.json()).toMatchObject({
      name: "Container Capability Service",
      worker: "${workerName}",
      meshExample: {
        image: "docker.io/library/alpine:3.22.1",
        binding: "MESH_DEMO_CONTAINER"
      }
    });
  });
});
`,
    "README.md": `# Dockerfile and Mesh-ready Container Service

This workerspace demonstrates Workerstation's Cloudflare Container binding:

- native \`wrangler.jsonc\` \`containers\` plus matching Durable Object bindings
- a local Dockerfile with \`image_build_context: "."\` and \`.dockerignore\`
- an approved \`docker.io/library/alpine:3.22.1\` remote-image example
- \`Container\`, \`getContainer\`, \`switchPort\`, and named instance identity
- HTTP and WebSocket forwarding on port 8080
- environment variables, health checks, ephemeral files, and lifecycle controls
- \`startAndWaitForPorts()\`, \`getState()\`, \`stop()\`, and \`destroy()\`

Request \`/mesh-container/health\` to run the Alpine example. It uses the same
Container API under both backends: Sandbox remains the default, while an
operator-provisioned Workerstation with \`CONTAINER_BACKEND=mesh\` transparently
routes it through Workers VPC, the Mesh connector, Kata, and Sysbox. Do not add
Mesh credentials to this workerspace; backend selection and signing belong to
the outer Workerstation boundary.

Run Preview and request \`/container/health\` for the Dockerfile example, or use
the dashboard in workerspace 2. Always destroy instances after automated tests.
Local Docker-in-Docker requires a runtime that permits nested user namespaces;
the deployed platform test is the source of truth when local Docker blocks it.
`,
    "package.json": JSON.stringify(
      {
        name: workerName,
        private: true,
        type: "module",
        main: "src/index.ts",
        scripts: {
          dev: "wrangler dev",
          deploy: "wrangler deploy",
          test: "vitest"
        },
        dependencies: { "@cloudflare/containers": "0.3.7" },
        devDependencies: {
          "@cloudflare/vitest-pool-workers": "^0.18.4",
          vitest: "^4.1.0",
          wrangler: "^4.110.0"
        }
      },
      null,
      2
    )
  };
}

// Second default workerspace: a Hono + JSX dashboard that consumes the
// first workerspace through station-local Worker bindings.
export function makeHonoDefaultFiles(api?: {
  sessionId: string;
  workerName: string;
  containerWorkerName?: string;
  mountPath?: string;
}): PlaygroundFiles {
  const workerName = `counter-dashboard-${Date.now()}`;
  const mountPath = api?.mountPath?.replace(/\/$/, "") ?? "";
  const ownBase = api
    ? `${mountPath}/${api.sessionId}/${workerName}/`.replace(/\/{2,}/g, "/")
    : "/";
  // Browser requests stay on this dashboard; its Worker binding forwards API
  // and WebSocket traffic to the sibling counter Worker.
  const apiUrl = ownBase;
  return {
    "wrangler.config.ts": WRANGLER_BUILD_CONFIG,
    "src/index.tsx": `import { WorkerEntrypoint, env } from "cloudflare:workers";
import { Hono } from "hono";
import { Fragment, jsx } from "hono/jsx";
import { jsxRenderer } from "hono/jsx-renderer";
import { Link } from "vite-ssr-components/hono";

// URLs are resolved relative to the served document so the same Worker works
// path-mounted and at the root of its own preview subdomain.
const { apiUrl } = env.CONFIG ?? { apiUrl: "" };
const clientScript = ${JSON.stringify(COUNTER_CLIENT_SOURCE)};

const app = new Hono()
  .use(
    jsxRenderer(({ children }) => (
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Realtime Durable Counter</title>
          <Link href="src/style.css" rel="stylesheet" />
        </head>
        <body>{children}</body>
      </html>
    ))
  )
  .get("/", (c) => {
    const configJson = JSON.stringify({ apiUrl }).replace(/</g, "\\u003c");
    return c.render(
      <main class="shell">
        <header>
          <span class="badge">workerspace 2 · hono + jsx</span>
          <h1>Realtime <em>Durable Counter</em></h1>
          <p class="sub">
            A Worker binding forwards HTTP and WebSocket traffic to workerspace
            1. Only its hibernating socket updates the number below.
          </p>
          <span id="connection-state" class="connection">offline</span>
        </header>

        <section class="counter-card">
          <button data-delta="-1" aria-label="Decrement counter">−</button>
          <div class="counter-readout">
            <output id="counter-value" aria-live="polite">…</output>
            <small id="counter-revision">waiting for socket</small>
          </div>
          <button data-delta="1" aria-label="Increment counter">+</button>
        </section>
        <p id="sync-status" class="sync-status">
          Connecting to the Durable Object…
        </p>

        <section class="workflow-card">
          <div>
            <span class="eyebrow">Dynamic Workflow</span>
            <h2>Launch a tiny counter parade</h2>
            <p class="sub">
              Parallel steps choose a theme and calculate confetti, then the
              Workflow asks the Durable Object to broadcast the result.
            </p>
          </div>
          <button id="celebrate" class="celebrate" aria-label="Celebrate with Workflow">
            Celebrate ✨
          </button>
          <label class="shoutout">
            <span>Add a shoutout — sent to the running Workflow via sendEvent:</span>
            <input id="celebrate-shoutout" type="text" maxlength="80" placeholder="Confetti inbound!" />
          </label>
          <ol class="workflow-stages" aria-label="Workflow progress">
            <li data-workflow-stage="queued">Queued</li>
            <li data-workflow-stage="running">Parallel magic</li>
            <li data-workflow-stage="broadcast">WebSocket broadcast</li>
          </ol>
          <p id="workflow-status" class="workflow-status">Ready when you are.</p>
        </section>

        <section class="browser-card">
          <div>
            <span class="eyebrow">Browser Run binding</span>
            <h2>Render the live counter in server-side Chrome</h2>
            <p class="sub">
              The dashboard calls the API through <code>COUNTER_API</code>, then
              <code>BROWSER.quickAction()</code> turns generated HTML into a PNG.
            </p>
          </div>
          <div class="browser-actions">
            <button id="render-browser" class="browser-render">
              Render with Browser
            </button>
            <button id="record-tour" class="browser-render">
              Record a Browser tour
            </button>
          </div>
          <img
            id="browser-preview"
            class="browser-preview"
            alt="Browser-rendered counter card"
            hidden
          />
          <p id="browser-status" class="browser-status">
            Browser Run is idle until you request a snapshot.
          </p>
          <p id="tour-status" class="browser-status">
            Record a driven session to watch it in the Flow recordings tab.
          </p>
        </section>

        <section class="capabilities-card">
          <div>
            <span class="eyebrow">KV + custom ASSETS</span>
            <h2>Inspect a scoped feature flag and static manifest</h2>
            <p class="sub">
              The API uses <code>KV.put/getWithMetadata/list</code> and fetches
              <code>public/capabilities.json</code> through <code>env.ASSETS</code>.
            </p>
          </div>
          <button id="inspect-capabilities" class="capability-inspect">
            Inspect KV + Assets
          </button>
          <pre id="capabilities-output" class="demo-output" hidden />
          <p id="capabilities-status" class="capabilities-status">
            No KV keys or static assets have been read yet.
          </p>
        </section>

        <section class="container-card">
          <div>
            <span class="eyebrow">Dockerfile Container + Worker binding</span>
            <h2>Run an isolated Node service from workerspace 3</h2>
            <p class="sub">
              <code>CONTAINER_API</code> lazily builds a Dockerfile, forwards HTTP
              and WebSockets, and exposes explicit lifecycle controls.
            </p>
          </div>
          <div class="container-actions">
            <button id="run-container" class="container-run">
              Run Docker demo
            </button>
            <button id="destroy-container" class="container-destroy">
              Destroy Container
            </button>
          </div>
          <pre id="container-output" class="demo-output" hidden />
          <p id="container-status" class="container-status">
            The paid Container remains stopped until requested.
          </p>
        </section>

        <footer>
          Worker binding: <code>COUNTER_API → ${api?.workerName ?? "counter-api"}</code>
          <br />Container binding: <code>CONTAINER_API → ${api?.containerWorkerName ?? "docker-service"}</code>
          <br />Mutation archive: <code>Queue → R2</code>
          <br />Feature config: <code>KV + ASSETS</code>
          <br />Server rendering: <code>BROWSER.quickAction()</code>
        </footer>
        <script
          id="counter-config"
          type="application/json"
          dangerouslySetInnerHTML={{ __html: configJson }}
        />
        <script
          type="module"
          dangerouslySetInnerHTML={{ __html: clientScript }}
        />
      </main>
    );
  });

export default class CounterDashboard extends WorkerEntrypoint<Env> {
  async fetch(request: Request) {
    const pathname = new URL(request.url).pathname;
    if (pathname.startsWith("/container/")) {
      return env.CONTAINER_API.fetch(request);
    }
    if (pathname.startsWith("/api/")) {
      return env.COUNTER_API.fetch(request);
    }
    return app.fetch(request, env, this.ctx);
  }
}
`,
    "src/style.css": `* { box-sizing: border-box; }

:root { color-scheme: dark; font-family: Inter, ui-sans-serif, system-ui, sans-serif; }

body {
  min-height: 100vh;
  margin: 0;
  display: grid;
  place-items: center;
  color: #eef2ff;
  background:
    radial-gradient(55rem 35rem at 5% -10%, #25429a 0%, transparent 55%),
    radial-gradient(48rem 32rem at 105% 110%, #7b1e57 0%, transparent 52%),
    #080b18;
}

.shell { width: min(46rem, calc(100vw - 2rem)); padding: 2rem 0; }

header, .counter-card, .workflow-card, .browser-card, .capabilities-card, .container-card {
  border: 1px solid rgba(255, 255, 255, 0.13);
  background: rgba(13, 18, 40, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
}

header { position: relative; padding: 1.8rem 2rem; border-radius: 1.4rem 1.4rem 0 0; }
.badge, .eyebrow { color: #9fb4ff; font-size: .69rem; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; }
h1 { margin: .65rem 0 .4rem; font-size: clamp(2rem, 6vw, 3.35rem); line-height: 1; }
h1 em { font-style: normal; background: linear-gradient(90deg, #67e8f9, #c084fc, #fb7185); background-clip: text; color: transparent; }
.sub { margin: .35rem 0 0; color: #aab3cc; line-height: 1.55; }
.connection { position: absolute; right: 1.5rem; top: 1.5rem; padding: .3rem .6rem; border-radius: 999px; background: #2b324b; color: #aab3cc; font: 700 .68rem ui-monospace, monospace; text-transform: uppercase; }
.connection[data-state="live"] { background: rgba(52, 211, 153, .14); color: #6ee7b7; }
.connection[data-state="connecting"] { color: #fbbf24; }

.counter-card { display: grid; grid-template-columns: 5rem 1fr 5rem; align-items: stretch; margin-top: .8rem; overflow: hidden; }
.counter-card button { border: 0; color: #eef2ff; background: rgba(255,255,255,.04); font-size: 2.1rem; cursor: pointer; transition: background .2s, transform .12s; }
.counter-card button:hover:not(:disabled) { background: rgba(125,211,252,.13); }
.counter-card button:active:not(:disabled) { transform: scale(.94); }
.counter-card button:disabled { opacity: .35; cursor: wait; }
.counter-readout { min-height: 9.5rem; display: grid; place-content: center; text-align: center; border-inline: 1px solid rgba(255,255,255,.1); }
#counter-value { font: 800 5rem/1 ui-monospace, monospace; transition: transform .25s, color .25s; }
#counter-value.changed { color: #67e8f9; transform: scale(1.12); }
#counter-revision { margin-top: .55rem; color: #78839f; font: .7rem ui-monospace, monospace; text-transform: uppercase; }
.sync-status { margin: 0; padding: .65rem 1rem; border-radius: 0 0 .8rem .8rem; background: rgba(0,0,0,.28); color: #fbbf24; font-size: .78rem; }
.sync-status[data-state="synced"] { color: #6ee7b7; }
.sync-status[data-state="error"] { color: #fb7185; }

.workflow-card { margin-top: .8rem; padding: 1.5rem; border-radius: 1rem; display: grid; grid-template-columns: 1fr auto; gap: 1rem; }
.workflow-card h2 { margin: .3rem 0; font-size: 1.2rem; }
.celebrate { align-self: center; border: 1px solid rgba(192,132,252,.45); border-radius: .75rem; padding: .8rem 1rem; color: white; background: linear-gradient(135deg, #6d28d9, #be185d); cursor: pointer; font-weight: 700; }
.celebrate:disabled { opacity: .5; cursor: wait; }
.shoutout { grid-column: 1 / -1; display: flex; flex-direction: column; gap: .3rem; color: #aab3cc; font-size: .78rem; }
.shoutout input { padding: .5rem .6rem; border-radius: .5rem; border: 1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.04); color: #eef2ff; font-size: .9rem; }
.workflow-stages { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, 1fr); gap: .45rem; margin: .25rem 0 0; padding: 0; list-style: none; }
.workflow-stages li { padding: .55rem; border-radius: .5rem; background: rgba(255,255,255,.045); color: #66708d; text-align: center; font-size: .72rem; }
.workflow-stages li.active { background: rgba(103,232,249,.1); color: #67e8f9; }
.workflow-status { grid-column: 1 / -1; margin: 0; color: #aab3cc; font-size: .82rem; }
.workflow-status[data-state="complete"] { color: #6ee7b7; }
.workflow-status[data-state="error"] { color: #fb7185; }

.browser-card { margin-top: .8rem; padding: 1.5rem; border-radius: 1rem; display: grid; grid-template-columns: 1fr auto; gap: 1rem; }
.browser-card h2 { margin: .3rem 0; font-size: 1.2rem; }
.browser-actions { display: flex; flex-wrap: wrap; gap: .6rem; justify-content: center; }
.browser-render { align-self: center; border: 1px solid rgba(103,232,249,.45); border-radius: .75rem; padding: .8rem 1rem; color: #08111f; background: linear-gradient(135deg, #67e8f9, #a78bfa); cursor: pointer; font-weight: 800; }
.browser-render:disabled { opacity: .5; cursor: wait; }
.browser-preview { grid-column: 1 / -1; width: 100%; border: 1px solid rgba(103,232,249,.25); border-radius: .75rem; }
.browser-preview[hidden] { display: none; }
.browser-status { grid-column: 1 / -1; margin: 0; color: #aab3cc; font-size: .82rem; }
.browser-status[data-state="running"] { color: #fbbf24; }
.browser-status[data-state="complete"] { color: #6ee7b7; }
.browser-status[data-state="error"] { color: #fb7185; }

.capabilities-card, .container-card { margin-top: .8rem; padding: 1.5rem; border-radius: 1rem; display: grid; grid-template-columns: 1fr auto; gap: 1rem; }
.capabilities-card h2, .container-card h2 { margin: .3rem 0; font-size: 1.2rem; }
.capability-inspect, .container-run, .container-destroy { align-self: center; border-radius: .75rem; padding: .8rem 1rem; cursor: pointer; font-weight: 800; }
.capability-inspect { border: 1px solid rgba(110,231,183,.45); color: #052e24; background: linear-gradient(135deg,#6ee7b7,#67e8f9); }
.container-actions { display: flex; align-items: center; gap: .5rem; }
.container-run { border: 1px solid rgba(251,191,36,.5); color: #241405; background: linear-gradient(135deg,#fbbf24,#fb7185); }
.container-destroy { border: 1px solid rgba(251,113,133,.4); color: #fecdd3; background: rgba(127,29,29,.35); }
.capability-inspect:disabled, .container-run:disabled, .container-destroy:disabled { opacity: .5; cursor: wait; }
.demo-output { grid-column: 1 / -1; max-height: 18rem; margin: 0; padding: .9rem; overflow: auto; border: 1px solid rgba(255,255,255,.1); border-radius: .65rem; color: #c4f1ff; background: #050816; font: .72rem/1.55 ui-monospace,monospace; white-space: pre-wrap; }
.demo-output[hidden] { display: none; }
.capabilities-status, .container-status { grid-column: 1 / -1; margin: 0; color: #aab3cc; font-size: .82rem; }
.capabilities-status[data-state="running"], .container-status[data-state="running"] { color: #fbbf24; }
.capabilities-status[data-state="complete"], .container-status[data-state="complete"] { color: #6ee7b7; }
.capabilities-status[data-state="error"], .container-status[data-state="error"] { color: #fb7185; }
footer { padding: .9rem .25rem; color: #65708c; font-size: .7rem; }
code { color: #9fb4ff; }

@media (max-width: 36rem) {
  .shell { padding: .7rem 0; }
  header { padding: 1.4rem; }
  .connection { position: static; display: inline-block; margin-top: 1rem; }
  .counter-card { grid-template-columns: 4rem 1fr 4rem; }
  .workflow-card, .browser-card, .capabilities-card, .container-card { grid-template-columns: 1fr; }
  .celebrate, .browser-render, .capability-inspect, .container-actions { justify-self: start; }
}
`,
    "test/page.test.ts": `import { SELF } from "cloudflare:test";
import { describe, expect, it } from "vitest";

describe("Counter dashboard runtime", () => {
  it("renders the WebSocket counter and Workflow controls", async () => {
    const response = await SELF.fetch("https://example.com/");
    const html = await response.text();

    expect(response.status).toBe(200);
    expect(html).toContain("Realtime");
    expect(html).toContain("Decrement counter");
    expect(html).toContain("Increment counter");
    expect(html).toContain("Celebrate with Workflow");
    expect(html).toContain("Render with Browser");
    expect(html).toContain("BROWSER.quickAction()");
    expect(html).toContain("Inspect KV + Assets");
    expect(html).toContain("Run Docker demo");
    expect(html).toContain("Destroy Container");
    expect(html).toContain("CONTAINER_API");
    expect(html).toContain("Worker binding");
    expect(html).toContain("Queue → R2");
    expect(html).toContain("counter-config");
    expect(html).toContain("api/counter/socket");
  });
});
`,
    "test/page.e2e.test.ts": `import { afterAll, beforeAll, describe, expect, it } from "vitest";
import { createTestHarness } from "wrangler";

const server = createTestHarness({
  workers: [
    {
      config: {
        name: "${workerName}",
        main: "src/index.tsx",
        compatibility_date: "2026-06-11",
        compatibility_flags: ["nodejs_compat"],
        tsconfig: "tsconfig.json",
        vars: {
          GREETING: "Hello from workerspace 2",
          CONFIG: { baseUrl: "/", apiUrl: "/counter-api/" }
        }
      }
    }
  ]
});

beforeAll(() => server.listen());
afterAll(() => server.close());

describe("Counter dashboard E2E", () => {
  it("serves the production-style interactive page", async () => {
    const response = await server.fetch("/");
    const html = await response.text();

    expect(response.status).toBe(200);
    expect(response.headers.get("content-type")).toContain("text/html");
    expect(html).toContain("counter-config");
    expect(html).toContain("Workflow progress");
    expect(html).toContain("Render with Browser");
    expect(html).toContain("Inspect KV + Assets");
    expect(html).toContain("Run Docker demo");
    expect(html).toContain("new WebSocket");
  });
});
`,
    "cloudflare.config.ts": `import { defineWorker, bindings } from "wrangler/experimental-config";
import * as entrypoint from "./src/index.tsx" with { type: "cf-worker" };

export default defineWorker({
  name: "${workerName}",
  entrypoint,
  compatibilityDate: "2026-06-11",
  compatibilityFlags: ["nodejs_compat"],
  env: {
    GREETING: bindings.text("Hello from workerspace 2"),
    CONFIG: bindings.json({
      baseUrl: "${ownBase}",
      apiUrl: "${apiUrl}"
    }),
    COUNTER_API: bindings.worker({
      workerName: "${api?.workerName ?? "counter-api"}"
    }),
    CONTAINER_API: bindings.worker({
      workerName: "${api?.containerWorkerName ?? "docker-service"}"
    })
  }
});
`,
    "vitest.config.ts": `import { cloudflareTest } from "@cloudflare/vitest-pool-workers";
import { defineConfig } from "vitest/config";

export default defineConfig({
  test: {
    projects: [
      {
        plugins: [
          cloudflareTest({
            main: "./src/index.tsx",
            miniflare: {
              compatibilityDate: "2026-06-11",
              compatibilityFlags: ["nodejs_compat"],
              bindings: {
                GREETING: "Hello from workerspace 2",
                CONFIG: { baseUrl: "/", apiUrl: "/counter-api/" }
              }
            }
          })
        ],
        test: { name: "runtime", include: ["test/page.test.ts"] }
      },
      {
        test: {
          name: "e2e",
          environment: "node",
          include: ["test/page.e2e.test.ts"]
        }
      }
    ]
  }
});
`,
    "tsconfig.json": JSON.stringify(
      {
        compilerOptions: {
          target: "ES2022",
          module: "ESNext",
          moduleResolution: "Bundler",
          jsx: "react-jsx",
          jsxImportSource: "hono/jsx",
          strict: true,
          types: ["@cloudflare/workers-types"]
        }
      },
      null,
      2
    ),
    "package.json": JSON.stringify(
      {
        name: "counter-dashboard-worker",
        private: true,
        type: "module",
        main: "src/index.tsx",
        scripts: { test: "vitest" },
        dependencies: {
          hono: "4.12.28",
          "vite-ssr-components": "0.6.1"
        },
        devDependencies: {
          "@cloudflare/vitest-pool-workers": "^0.18.4",
          "@cloudflare/workers-types": "^5.20260710.1",
          vitest: "^4.1.0",
          wrangler: "^4.110.0"
        }
      },
      null,
      2
    )
  };
}

export { workerNameFromFiles } from "@workerstation/shared";
